import * as React from 'react';
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { IAppOnlyStore } from '../../stores';
import { PageBar, PortalSearchBarField } from '../../components/layout';
import { Button, EButtonType } from '../../components/action';
import { Tooltip } from '../../components/popup';
import { EUserTypeFilter, EUserStatusFiler } from '../../../../common/interfaces/users';

// Styles
const s = require('./UsersListPageBar.css');
const searchIcon = require('./../../assets/search-icon.svg');
const dropdownIcon = require('./../../assets/dropdown-arrow-revised.svg');

// Component
@inject('store')
@observer
export class UsersListPageBar extends React.Component<IAppOnlyStore, {}> {
    @observable private showUserTypeFilter: boolean = false;
    @observable private showUserStatusFilter: boolean = false;
    @observable private userTypeFilterPositionFromLeft: number = 0;
    @observable private userStatusFilterPositionFromLeft: number = 0;

    private userTypeFilterButton: Button = null;
    private userStatusFilterButton: Button = null;
    private referenceUserTypeFilterButton = ref => (this.userTypeFilterButton = ref);
    private referenceUserStatusFilterButton = ref => (this.userStatusFilterButton = ref);

    render() {
        const { users } = this.props.store;

        return (
            <div>
                <PageBar wrapClassName={s.topbarWrapper}>
                    <div>
                        <PortalSearchBarField
                            field="search"
                            className={s.usersSearchField}
                            value={users.usersSearch}
                            icon={searchIcon}
                            lightness={4}
                            autoFocus={true}
                            label="Search by user's name, email, cardcode..."
                            onChange={this.handleUsersSearchValueChange}
                        />

                        <Button
                            ref={this.referenceUserTypeFilterButton}
                            onClick={this.handleToggleUsersTypeFilterDropdown()}
                            className={s.userTypeFilterButton}
                            type={users.usersType === EUserTypeFilter.All ? EButtonType.BorderOnly : EButtonType.Default}
                            inBox={true}
                            icon={{
                                element: <img src={dropdownIcon} width={10} height={7} alt="" />
                            }}
                        >
                            {users.usersType === EUserTypeFilter.All
                                ? 'Show all user types'
                                : users.usersType === EUserTypeFilter.Admin
                                  ? 'Show administrators'
                                  : users.usersType === EUserTypeFilter.Internal
                                    ? 'Show internal users'
                                    : users.usersType === EUserTypeFilter.Portal
                                      ? 'Show portal customers'
                                      : 'Show other user types'}
                        </Button>

                        <Button
                            ref={this.referenceUserStatusFilterButton}
                            onClick={this.handleToggleUsersStatusFilterDropdown()}
                            className={s.userStatusFilterButton}
                            type={users.usersStatus === EUserStatusFiler.All ? EButtonType.BorderOnly : EButtonType.Default}
                            inBox={true}
                            icon={{
                                element: <img src={dropdownIcon} width={10} height={7} alt="" />
                            }}
                        >
                            {users.usersStatus === EUserStatusFiler.All
                                ? 'Show active and disabled users'
                                : users.usersStatus === EUserStatusFiler.Active
                                  ? 'Show only active users'
                                  : users.usersStatus === EUserStatusFiler.Disabled
                                    ? 'Show only disabled users'
                                    : 'Show other user statuses'}
                        </Button>
                    </div>
                    <div>
                        <Button inBox={true} type={EButtonType.Action} onClick={this.handleOpenCreateNewUserModal}>
                            Create new user
                        </Button>
                    </div>
                </PageBar>

                <div>{this.props.children}</div>

                <Tooltip
                    className={s.userTypesTooltip}
                    show={this.showUserTypeFilter}
                    style={{ left: this.userTypeFilterPositionFromLeft + 'px' }}
                    onClose={this.handleCloseUserTypeFilterDropdown}
                    title="Filter users by type"
                >
                    {Object.keys(EUserTypeFilter).map(key => {
                        const filter: EUserTypeFilter = EUserTypeFilter[key];
                        return (
                            <Button
                                key={key}
                                className={s.userTypesButton}
                                type={users.usersType === filter ? EButtonType.Default : EButtonType.Action}
                                onClick={this.handleFilterUserTypeChange(filter)}
                            >
                                {filter}
                            </Button>
                        );
                    })}
                </Tooltip>

                <Tooltip
                    className={s.userStatusTooltip}
                    show={this.showUserStatusFilter}
                    style={{ left: this.userStatusFilterPositionFromLeft + 'px' }}
                    onClose={this.handleCloseUserStatusFilterDropdown}
                    title="Filter users by status"
                >
                    {Object.keys(EUserStatusFiler).map(key => {
                        const filter: EUserStatusFiler = EUserStatusFiler[key];
                        return (
                            <Button
                                key={key}
                                className={s.userStatusButton}
                                type={users.usersStatus === filter ? EButtonType.Default : EButtonType.Action}
                                onClick={this.handleFilterUserStatusChange(filter)}
                            >
                                {filter}
                            </Button>
                        );
                    })}
                </Tooltip>
            </div>
        );
    }

    @action
    private handleUsersSearchValueChange = (field: string, value: string) => {
        this.props.store.users.usersSearch = value;
    };

    @action
    private handleToggleUsersTypeFilterDropdown = (isOpen?: boolean) => (e: React.MouseEvent<HTMLButtonElement>) => {
        if ((typeof isOpen !== 'undefined' && isOpen) || !this.showUserTypeFilter) {
            this.showUserTypeFilter = true;

            if (this.userTypeFilterButton && this.userTypeFilterButton.container) {
                this.userTypeFilterPositionFromLeft = this.userTypeFilterButton.container.offsetLeft;
            }
        } else {
            this.showUserTypeFilter = false;
        }
    };

    @action
    private handleCloseUserTypeFilterDropdown = () => {
        this.showUserTypeFilter = false;
    };

    @action
    private handleFilterUserTypeChange = (value: EUserTypeFilter) => (e: React.MouseEvent<HTMLButtonElement>) => {
        this.handleCloseUserTypeFilterDropdown();
        this.props.store.users.usersType = value;
    };

    @action
    private handleToggleUsersStatusFilterDropdown = (isOpen?: boolean) => (e: React.MouseEvent<HTMLButtonElement>) => {
        if ((typeof isOpen !== 'undefined' && isOpen) || !this.showUserStatusFilter) {
            this.showUserStatusFilter = true;

            if (this.userStatusFilterButton && this.userStatusFilterButton.container) {
                this.userStatusFilterPositionFromLeft = this.userStatusFilterButton.container.offsetLeft;
            }
        } else {
            this.showUserStatusFilter = false;
        }
    };

    @action
    private handleCloseUserStatusFilterDropdown = () => {
        this.showUserStatusFilter = false;
    };

    @action
    private handleFilterUserStatusChange = (value: EUserStatusFiler) => (e: React.MouseEvent<HTMLButtonElement>) => {
        this.handleCloseUserStatusFilterDropdown();
        this.props.store.users.usersStatus = value;
    };

    @action
    private handleOpenCreateNewUserModal = (e: React.MouseEvent<HTMLButtonElement>) => {
        this.props.store.users.openUserModal(null);
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/admin/UsersListPageBar.tsx